import { useState, useEffect, useMemo, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  User,
  MapPin,
  Phone,
  X,
  Save,
  Plus,
  Trash2,
  Camera,
  Settings,
  Mail,
  Bell,
  Trophy
} from 'lucide-react'
import { Modal, Button, Input, Badge } from '../ui'
import Avatar from '../ui/Avatar'
import { useAuthStore } from '../../stores/authStore'
import { useLoyaltyLevel, useShowMyLoyaltyFrame, useSetShowMyLoyaltyFrame, useAchievements } from '../../stores/loyaltyStore'
import ProgressBar from '../loyalty/ProgressBar'
import LoyaltyDashboard from '../loyalty/LoyaltyDashboard'
import { doc, updateDoc } from 'firebase/firestore'
import { db } from '../../lib/firebase'
import { isNicknameAvailable, updatePublicProfile } from '../../services/nicknameService'
import type { ShippingAddress } from '../../types/user'

interface UserProfileModalProps {
  isOpen: boolean
  onClose: () => void
}

const profileSchema = z.object({
  firstName: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  lastName: z.string().min(2, 'Los apellidos deben tener al menos 2 caracteres'),
  nickname: z.string()
    .min(3, 'El nickname debe tener al menos 3 caracteres')
    .max(20, 'El nickname no puede tener más de 20 caracteres')
    .regex(/^[a-zA-Z0-9_-]+$/, 'Solo se permiten letras, números, guiones y guiones bajos')
    .optional()
    .or(z.literal('')),
  phone: z.string().regex(/^(\+34|0034|34)?[6789]\d{8}$/, 'Número de teléfono español inválido').optional().or(z.literal('')),
})

const addressSchema = z.object({
  firstName: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  lastName: z.string().min(2, 'Los apellidos deben tener al menos 2 caracteres'),
  addressLine1: z.string().min(3, 'La calle/avenida debe tener al menos 3 caracteres'),
  number: z.string().min(1, 'El número es obligatorio'),
  addressLine2: z.string().min(1, 'La información adicional es obligatoria'),
  city: z.string().min(2, 'La ciudad debe tener al menos 2 caracteres'),
  state: z.string().min(2, 'La provincia debe tener al menos 2 caracteres'),
  postalCode: z.string().regex(/^\d{5}$/, 'El código postal debe tener 5 dígitos'),
  country: z.string().min(1, 'El país es obligatorio').default('España'),
  phone: z.string().min(9, 'El teléfono es obligatorio'),
  email: z.string().min(1, 'El email es obligatorio').email('Email inválido'),
})

type ProfileForm = z.infer<typeof profileSchema>
type AddressForm = z.infer<typeof addressSchema>

const UserProfileModal = ({ isOpen, onClose }: UserProfileModalProps) => {
  const [activeTab, setActiveTab] = useState<'profile' | 'loyalty' | 'addresses' | 'preferences'>('profile')
  const loyaltyLevel = useLoyaltyLevel()
  const showMyLoyaltyFrame = useShowMyLoyaltyFrame()
  const setShowMyLoyaltyFrame = useSetShowMyLoyaltyFrame()
  const achievements = useAchievements()
  const hasAnyUnlockedAchievements = useMemo(() => {
    return achievements.some(a => a.isUnlocked)
  }, [achievements])
  const [isLoading, setIsLoading] = useState(false)
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [editingAddress, setEditingAddress] = useState<ShippingAddress | null>(null)
  const [showAddressForm, setShowAddressForm] = useState(false)
  const { user, setUser } = useAuthStore()

  // Memoize default values to prevent unnecessary re-renders
  const profileDefaultValues = useMemo(() => ({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    nickname: user?.nickname || '',
    phone: user?.phoneNumber || ''
  }), [user?.firstName, user?.lastName, user?.nickname, user?.phoneNumber])

  const addressDefaultValues = useMemo(() => ({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    addressLine1: '',
    number: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'España',
    phone: user?.phoneNumber || '',
    email: (user?.email && user?.emailVerified) ? user.email : ''
  }), [user?.firstName, user?.lastName, user?.phoneNumber, user?.email, user?.emailVerified])

  // Profile form
  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: profileDefaultValues
  })

  // Address form
  const addressForm = useForm<AddressForm>({
    resolver: zodResolver(addressSchema),
    defaultValues: addressDefaultValues
  })

  // Reset forms when user changes
  useEffect(() => {
    if (user) {
      profileForm.reset({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phoneNumber || ''
      })

      addressForm.reset({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        addressLine1: '',
        number: '',
        addressLine2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'España',
        phone: user.phoneNumber || '',
        email: (user.email && user.emailVerified) ? user.email : ''
      })
    }
  }, [user, profileForm, addressForm])

  const handlePhotoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file
      if (!file.type.startsWith('image/')) {
        alert('Por favor selecciona un archivo de imagen válido')
        return
      }
      
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        alert('La imagen es demasiado grande. Máximo 2MB.')
        return
      }
      
      setPhotoFile(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        const base64Result = e.target?.result as string
        setPhotoPreview(base64Result)
        
        // Update user immediately for real-time preview
        if (user) {
          const updatedUser = {
            ...user,
            photoURL: base64Result
          }
          setUser(updatedUser)
        }
      }
      reader.readAsDataURL(file)
    }
  }, [user, setUser])

  const removePhoto = useCallback(() => {
    setPhotoFile(null)
    setPhotoPreview(null)
  }, [])

  const onProfileSubmit = useCallback(async (data: ProfileForm) => {
    if (!user) return

    setIsLoading(true)
    try {
      // Validate nickname uniqueness if provided
      if (data.nickname && data.nickname.trim()) {
        const isAvailable = await isNicknameAvailable(data.nickname.trim(), user.id)
        if (!isAvailable) {
          profileForm.setError('nickname', {
            type: 'manual',
            message: 'Este nickname ya está en uso. Por favor, elige otro.'
          })
          setIsLoading(false)
          return
        }
      }

      // Helper function to remove undefined values
      const removeUndefined = (obj: any) => {
        return Object.keys(obj).reduce((acc, key) => {
          if (obj[key] !== undefined) {
            acc[key] = obj[key]
          }
          return acc
        }, {} as any)
      }

      const userDoc = doc(db, 'users', user.id)
      const updateData = removeUndefined({
        firstName: data.firstName,
        lastName: data.lastName,
        nickname: data.nickname?.trim() || null, // Use null instead of undefined
        displayName: data.nickname?.trim() || `${data.firstName} ${data.lastName}`,
        phoneNumber: data.phone || '',
        photoURL: photoPreview || undefined // Will be filtered out if undefined
      })

      // Update user document
      await updateDoc(userDoc, updateData)

      // Update public profile - also filter undefined values
      const publicProfileData = removeUndefined({
        firstName: data.firstName,
        lastName: data.lastName,
        nickname: data.nickname?.trim() || null, // Use null instead of undefined
        photoURL: photoPreview || user.photoURL || null // Use null instead of undefined
      })

      await updatePublicProfile(user.id, publicProfileData)

      // Update local user state
      const updatedUser = {
        ...user,
        ...updateData
      }
      setUser(updatedUser)

      setPhotoFile(null)
      setPhotoPreview(null)
    } catch (error) {
      console.error('Error updating profile:', error)
      // Show error to user
      profileForm.setError('root', {
        type: 'manual',
        message: 'Error al actualizar el perfil. Inténtalo de nuevo.'
      })
    } finally {
      setIsLoading(false)
    }
  }, [user, profileForm, photoPreview, setUser])

  const onAddressSubmit = useCallback(async (data: AddressForm) => {
    if (!user) return

    setIsLoading(true)
    try {
      const newAddress: ShippingAddress = {
        ...data,
        isDefault: editingAddress?.isDefault || (user?.shippingAddresses || []).length === 0
      }

      let updatedAddresses = [...(user?.shippingAddresses || [])]
      
      if (editingAddress) {
        // Update existing address
        const index = updatedAddresses.findIndex(addr => 
          addr.addressLine1 === editingAddress.addressLine1 && 
          addr.city === editingAddress.city
        )
        if (index !== -1) {
          updatedAddresses[index] = newAddress
        }
      } else {
        // Add new address
        updatedAddresses.push(newAddress)
      }

      // Update default address if this one is marked as default
      let defaultAddress = user.defaultShippingAddress
      if (newAddress.isDefault) {
        defaultAddress = newAddress
        // Remove default flag from other addresses
        updatedAddresses = updatedAddresses.map(addr => ({
          ...addr,
          isDefault: addr === newAddress
        }))
      }

      const userDoc = doc(db, 'users', user.id)
      await updateDoc(userDoc, {
        shippingAddresses: updatedAddresses,
        defaultShippingAddress: defaultAddress
      })

      // Update local user state
      const updatedUser = {
        ...user,
        shippingAddresses: updatedAddresses,
        defaultShippingAddress: defaultAddress
      }
      setUser(updatedUser)

      setEditingAddress(null)
      setShowAddressForm(false)
      addressForm.reset({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        addressLine1: '',
        number: '',
        addressLine2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'España',
        phone: user.phoneNumber || '',
        email: (user.email && user.emailVerified) ? user.email : ''
      })
    } catch (error) {
      console.error('Error updating address:', error)
    } finally {
      setIsLoading(false)
    }
  }, [user, setUser, editingAddress, addressForm])

  const deleteAddress = async (addressToDelete: ShippingAddress) => {
    if (!user) return

    setIsLoading(true)
    try {
      const updatedAddresses = (user?.shippingAddresses || []).filter(addr => 
        !(addr.addressLine1 === addressToDelete.addressLine1 && 
          addr.city === addressToDelete.city)
      )

      let defaultAddress = user.defaultShippingAddress
      if (addressToDelete.isDefault && updatedAddresses.length > 0) {
        updatedAddresses[0].isDefault = true
        defaultAddress = updatedAddresses[0]
      } else if (addressToDelete.isDefault) {
        defaultAddress = undefined
      }

      const userDoc = doc(db, 'users', user.id)
      await updateDoc(userDoc, {
        shippingAddresses: updatedAddresses,
        defaultShippingAddress: defaultAddress || null
      })

      // Update local user state
      const updatedUser = {
        ...user,
        shippingAddresses: updatedAddresses,
        defaultShippingAddress: defaultAddress
      }
      setUser(updatedUser)
    } catch (error) {
      console.error('Error deleting address:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const setDefaultAddress = async (address: ShippingAddress) => {
    if (!user) return

    setIsLoading(true)
    try {
      const updatedAddresses = (user?.shippingAddresses || []).map(addr => ({
        ...addr,
        isDefault: addr.addressLine1 === address.addressLine1 && addr.city === address.city
      }))

      const userDoc = doc(db, 'users', user.id)
      await updateDoc(userDoc, {
        shippingAddresses: updatedAddresses,
        defaultShippingAddress: { ...address, isDefault: true }
      })

      // Update local user state
      const updatedUser = {
        ...user,
        shippingAddresses: updatedAddresses,
        defaultShippingAddress: { ...address, isDefault: true }
      }
      setUser(updatedUser)
    } catch (error) {
      console.error('Error setting default address:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const editAddress = (address: ShippingAddress) => {
    setEditingAddress(address)
    addressForm.reset({
      firstName: address.firstName,
      lastName: address.lastName,
      addressLine1: address.addressLine1,
      number: address.number || '',
      addressLine2: address.addressLine2,
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
      country: address.country,
      phone: address.phone,
      email: address.email || ''
    })
    setShowAddressForm(true)
  }

  const addNewAddress = useCallback(() => {
    setEditingAddress(null)
    addressForm.reset({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      addressLine1: '',
      number: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'España',
      phone: user?.phoneNumber || '',
      email: (user?.email && user?.emailVerified) ? user.email : ''
    })
    setShowAddressForm(true)
  }, [user, addressForm])

  const tabs = useMemo(() => [
    { id: 'profile', label: 'Perfil', icon: User },
    { id: 'loyalty', label: 'Lealtad', icon: Trophy },
    { id: 'addresses', label: 'Direcciones', icon: MapPin },
    { id: 'preferences', label: 'Preferencias', icon: Settings }
  ], [])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="relative inline-block mb-4">
            <Avatar
              src={photoPreview || user?.photoURL}
              name={user?.firstName || user?.displayName || user?.email || 'Usuario'}
              size="xl"
              loyaltyLevel={loyaltyLevel}
              showLoyaltyFrame={hasAnyUnlockedAchievements && showMyLoyaltyFrame}
              className="w-24 h-24"
            />
            {photoPreview && (
              <button
                type="button"
                onClick={() => {
                  setPhotoFile(null)
                  setPhotoPreview(null)

                  // Restore original photo immediately
                  if (user) {
                    const updatedUser = {
                      ...user,
                      photoURL: user.photoURL?.startsWith('data:') ? '' : user.photoURL || ''
                    }
                    setUser(updatedUser)
                  }
                }}
                className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors z-10"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => document.getElementById('profile-photo-upload')?.click()}
              className="absolute -bottom-2 -right-2 p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors shadow-lg z-10"
            >
              <Camera className="w-4 h-4" />
            </button>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">
            Mi Perfil
          </h2>
          <p className="text-gray-600">
            Gestiona tu información personal y preferencias
          </p>
        </div>

        {/* Photo Upload Input */}
        <input
          type="file"
          accept="image/*"
          onChange={handlePhotoChange}
          className="hidden"
          id="profile-photo-upload"
        />

        {/* Tabs */}
        <div className="flex space-x-1 bg-gray-100 rounded-xl p-1 overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`flex-shrink-0 flex items-center justify-center gap-1 sm:gap-2 py-2 sm:py-3 px-2 sm:px-4 rounded-lg font-medium text-xs sm:text-sm transition-all min-w-0 ${
                  activeTab === tab.id
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="hidden xs:inline sm:inline whitespace-nowrap">{tab.label}</span>
              </button>
            )
          })}
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px]">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <Input
                  label="Nombre"
                  placeholder="Tu nombre"
                  {...profileForm.register('firstName')}
                  error={profileForm.formState.errors.firstName?.message}
                  leftIcon={<User className="w-5 h-5" />}
                />
                <Input
                  label="Apellidos"
                  placeholder="Tus apellidos"
                  {...profileForm.register('lastName')}
                  error={profileForm.formState.errors.lastName?.message}
                  leftIcon={<User className="w-5 h-5" />}
                />
              </div>

              <Input
                label="Nickname (opcional)"
                placeholder="Tu nickname único para los sorteos"
                {...profileForm.register('nickname')}
                error={profileForm.formState.errors.nickname?.message}
                leftIcon={<User className="w-5 h-5" />}
                helperText="Se mostrará en lugar de tu nombre real en los sorteos. Solo letras, números, guiones y guiones bajos."
              />

              <Input
                label="Email"
                type="email"
                value={user?.email || ''}
                disabled={!!(user?.email && user?.emailVerified)}
                leftIcon={<Mail className="w-5 h-5" />}
                helperText={user?.email && user?.emailVerified ? "Email verificado - no se puede modificar" : undefined}
              />

              <Input
                label="Teléfono"
                placeholder="+34 123 456 789"
                type="tel"
                {...profileForm.register('phone')}
                error={profileForm.formState.errors.phone?.message}
                leftIcon={<Phone className="w-5 h-5" />}
                disabled={!!user?.phoneNumber}
                helperText={user?.phoneNumber ? "Teléfono verificado - no se puede modificar" : undefined}
              />

              <Button
                type="submit"
                className="w-full"
                isLoading={isLoading}
                leftIcon={<Save className="w-5 h-5" />}
              >
                Guardar Cambios
              </Button>
            </form>
          )}

          {/* Loyalty Tab */}
          {activeTab === 'loyalty' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="text-center mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                  Sistema de Lealtad
                </h3>
                <p className="text-sm sm:text-base text-gray-600">
                  Gana puntos con cada compra y desbloquea logros exclusivos
                </p>
              </div>

              {/* Loyalty Frame Toggle - Only show if user has unlocked achievements */}
              {hasAnyUnlockedAchievements && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4">
                  <div className="flex items-start sm:items-center justify-between gap-3">
                    <div className="flex items-start sm:items-center gap-2 sm:gap-3 flex-1 min-w-0">
                      <Trophy className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5 sm:mt-0" />
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-blue-900 text-sm sm:text-base">
                          Marco de Logro
                        </h4>
                        <p className="text-blue-700 text-xs sm:text-sm break-words">
                          Controla si tu marco de logro se muestra a otros usuarios
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={showMyLoyaltyFrame}
                        onChange={(e) => setShowMyLoyaltyFrame(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              )}

              {/* Progress Section */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-2xl p-3 sm:p-6">
                <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                  <Trophy className="w-4 sm:w-5 h-4 sm:h-5 text-purple-600" />
                  <span className="text-sm sm:text-base">Tu Progreso</span>
                </h4>
                <ProgressBar
                  showLabel={true}
                  showPoints={true}
                  size="lg"
                  variant="premium"
                  animated={true}
                />
              </div>

              {/* Full Loyalty Dashboard */}
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <LoyaltyDashboard embedded={true} onClose={onClose} />
              </div>
            </div>
          )}

          {/* Addresses Tab */}
          {activeTab === 'addresses' && (
            <div className="space-y-4 sm:space-y-6">
              {!showAddressForm ? (
                <>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Mis Direcciones
                    </h3>
                    <Button
                      onClick={addNewAddress}
                      variant="outline"
                      size="sm"
                      leftIcon={<Plus className="w-4 h-4" />}
                      className="w-full sm:w-auto"
                    >
                      Agregar Dirección
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {(user?.shippingAddresses || []).map((address, index) => (
                      <div
                        key={`address-${index}-${address.firstName}-${address.lastName}-${address.addressLine1}`}
                        className={`p-4 rounded-xl border-2 transition-colors ${
                          address.isDefault
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="font-semibold text-gray-900">
                                {address.firstName} {address.lastName}
                              </div>
                              {address.isDefault && (
                                <Badge variant="success" className="text-xs">
                                  Por defecto
                                </Badge>
                              )}
                            </div>
                            <div className="text-gray-700 text-sm space-y-1">
                              <div>{address.addressLine1}</div>
                              {address.addressLine2 && <div>{address.addressLine2}</div>}
                              <div>
                                {address.city}, {address.state} {address.postalCode}
                              </div>
                              <div>{address.country}</div>
                              {address.phone && <div>📞 {address.phone}</div>}
                              {address.email && <div>📧 {address.email}</div>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            {!address.isDefault && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDefaultAddress(address)}
                                className="text-blue-600"
                              >
                                Hacer por defecto
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => editAddress(address)}
                            >
                              Editar
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteAddress(address)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}

                    {(user?.shippingAddresses || []).length === 0 && (
                      <div className="text-center py-12">
                        <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          Sin direcciones guardadas
                        </h3>
                        <p className="text-gray-600 mb-6">
                          Agrega una dirección para recibir tus premios
                        </p>
                        <Button
                          onClick={addNewAddress}
                          leftIcon={<Plus className="w-5 h-5" />}
                        >
                          Agregar Primera Dirección
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <form onSubmit={addressForm.handleSubmit(onAddressSubmit)} className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {editingAddress ? 'Editar Dirección' : 'Nueva Dirección'}
                    </h3>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowAddressForm(false)
                        setEditingAddress(null)
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Nombre"
                      placeholder="Nombre"
                      {...addressForm.register('firstName')}
                      error={addressForm.formState.errors.firstName?.message}
                    />
                    <Input
                      label="Apellidos"
                      placeholder="Apellidos"
                      {...addressForm.register('lastName')}
                      error={addressForm.formState.errors.lastName?.message}
                    />
                  </div>

                  <Input
                    label="Calle / Avenida *"
                    placeholder="Calle de la Paz, Avenida de la Libertad..."
                    {...addressForm.register('addressLine1')}
                    error={addressForm.formState.errors.addressLine1?.message}
                  />

                  <div className="grid grid-cols-3 gap-4">
                    <Input
                      label="Número *"
                      placeholder="123"
                      {...addressForm.register('number')}
                      error={addressForm.formState.errors.number?.message}
                    />
                    <div className="col-span-2">
                      <Input
                        label="Información adicional *"
                        placeholder="Piso 3, Puerta A, Portal 2..."
                        {...addressForm.register('addressLine2')}
                        error={addressForm.formState.errors.addressLine2?.message}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="Ciudad"
                      placeholder="Tu ciudad"
                      {...addressForm.register('city')}
                      error={addressForm.formState.errors.city?.message}
                    />
                    <Input
                      label="Provincia"
                      placeholder="Tu provincia"
                      {...addressForm.register('state')}
                      error={addressForm.formState.errors.state?.message}
                    />
                    <Input
                      label="Código postal"
                      placeholder="12345"
                      {...addressForm.register('postalCode')}
                      error={addressForm.formState.errors.postalCode?.message}
                    />
                  </div>

                  <Input
                    label="Teléfono *"
                    placeholder="+34 600 000 000"
                    type="tel"
                    {...addressForm.register('phone')}
                    error={addressForm.formState.errors.phone?.message}
                    leftIcon={<Phone className="w-5 h-5" />}
                    disabled={!!user?.phoneNumber}
                    helperText={user?.phoneNumber ? "Teléfono verificado auto-rellenado" : undefined}
                  />

                  <Input
                    label="Email para esta dirección *"
                    placeholder="email@ejemplo.com"
                    type="email"
                    {...addressForm.register('email')}
                    error={addressForm.formState.errors.email?.message}
                    leftIcon={<Mail className="w-5 h-5" />}
                    disabled={!!(user?.email && user?.emailVerified)}
                    helperText={user?.email && user?.emailVerified ? "Email verificado auto-rellenado" : "Email obligatorio para notificaciones de envío"}
                  />

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setShowAddressForm(false)
                        setEditingAddress(null)
                      }}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1"
                      isLoading={isLoading}
                      leftIcon={<Save className="w-5 h-5" />}
                    >
                      {editingAddress ? 'Actualizar' : 'Agregar'} Dirección
                    </Button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Preferencias
              </h3>

              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Bell className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-blue-900 mb-2">
                        Notificaciones
                      </h4>
                      <div className="space-y-3">
                        <label className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            defaultChecked={user?.preferences.notifications.email}
                            className="rounded border-gray-300"
                          />
                          <div>
                            <div className="font-medium text-blue-800">Email</div>
                            <div className="text-blue-700 text-sm">Recibe actualizaciones por email</div>
                          </div>
                        </label>
                        <label className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            defaultChecked={user?.preferences.notifications.push}
                            className="rounded border-gray-300"
                          />
                          <div>
                            <div className="font-medium text-blue-800">Push</div>
                            <div className="text-blue-700 text-sm">Notificaciones del navegador</div>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-center py-8 text-gray-500">
                  <Settings className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p>Más preferencias próximamente...</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end pt-6 border-t border-gray-200">
          <Button variant="ghost" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default UserProfileModal